import React, { useState, useEffect } from 'react';

/**
 * ReplayTourButton
 * A reusable "Tour this page" trigger that can be placed in any page section
 * or drawer footer to let users replay the current page's guided tour.
 *
 * Tour-awareness:
 * The button listens for 'tour-presence' events dispatched by GuidedTour.
 * It only renders when a tour is available on the current page (window.__guidedTourAvailable).
 * This means you can place this component anywhere (e.g. SupportHelpDrawer, Header) and it
 * will automatically appear/disappear as the user navigates between pages with and without tours.
 *
 * Props:
 * - onReplayTour: fn — called on click. Defaults to dispatching a 'replay-tour' CustomEvent
 *   on window, which GuidedTour listens for via its onReplayRequest prop.
 * - label: string — button text (default: "Tour this page")
 * - className: string — additional wrapper classes
 *
 * Theming: Uses succession-secondary brand color per theming guide.
 */
const ReplayTourButton = ({
  onReplayTour,
  label = 'Tour this page',
  className = '',
}) => {
  const [tourAvailable, setTourAvailable] = useState(!!window.__guidedTourAvailable);

  useEffect(() => {
    const handler = (e) => setTourAvailable(e.detail?.available ?? false);
    window.addEventListener('tour-presence', handler);
    // Sync in case the event fired before this component mounted
    setTourAvailable(!!window.__guidedTourAvailable);
    return () => window.removeEventListener('tour-presence', handler);
  }, []);

  if (!tourAvailable) return null;

  const handleClick = () => {
    if (onReplayTour) {
      onReplayTour();
    } else {
      window.dispatchEvent(new CustomEvent('replay-tour'));
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full transition-all duration-200 ${className}`}
      style={{
        border: '1px solid var(--succession-border, #e5e7eb)',
        color: 'var(--succession-text-secondary, #6b7280)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = 'var(--succession-secondary, #002949)';
        e.currentTarget.style.borderColor = 'var(--succession-secondary, #002949)';
        e.currentTarget.style.backgroundColor = 'rgba(var(--succession-secondary-rgb, 0,41,73), 0.05)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = 'var(--succession-text-secondary, #6b7280)';
        e.currentTarget.style.borderColor = 'var(--succession-border, #e5e7eb)';
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
      aria-label={label}
    >
      {/* RotateCcw icon (matches Polymet's lucide RotateCcwIcon) */}
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="1 4 1 10 7 10" />
        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
      </svg>
      <span>{label}</span>
    </button>
  );
};

export default ReplayTourButton;
